import React, { useCallback, useEffect, useState } from "react";
import t from "../../../../../../../i18n";
import Button from "@ui/Button";
import { Text } from "@ui/Text";
import { AddCircleLine, DeleteBinLine, UploadLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import type { BlockImageModelT } from "../../../../schema/block/image";
import useModal from "@ui/lib/hooks/useModal";
import { MTS_ENV } from "../../../../../../../lib/setupEnv";
import type { BlockDiagramModelT } from "../../../../schema/block/diagram";
import { BlockFileUploadModal } from "./BlockFileUploadModal";

export function BlockFile({
  projectId,
  block,
  deletable = false,
}: {
  projectId: string;
  deletable?: boolean;
  block: BlockImageModelT | BlockDiagramModelT;
}) {
  const editor = useDashboardEditorContext();
  const { modal: modalOpened, openModal, closeModal } = useModal();

  const updatePath = useCallback(
    (path: string) => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: { ...block.config, url: path },
      } as BlockImageModelT | BlockDiagramModelT);
    },
    [block, editor],
  );

  const removeImage = useCallback(() => {
    if (!block) return;
    void editor?.updateBlock({ ...block, config: { ...block.config, url: "" } } as
      | BlockImageModelT
      | BlockDiagramModelT);
  }, [block, editor]);

  const [fileSize, setFileSize] = useState<number | null>(null);
  useEffect(() => {
    /**
     * @note We can't be sync with the file drop as React will update before window.performance entries.
     * So we wait for the image load event to be sure it is present.
     */
    const img = new Image();
    img.onload = () => {
      setFileSize(() => {
        const src = encodeURI(`${MTS_ENV.COREV2_ASSETS_URL}/${block?.config.url}`);
        const { decodedBodySize = 0 } =
          (window.performance.getEntriesByName(src)[0] as PerformanceResourceTiming | null) ?? {};
        return Math.round(decodedBodySize / 1024);
      });
    };
    img.src = `${MTS_ENV.COREV2_ASSETS_URL}/${block?.config.url}`;
  }, [block?.config.url]);

  return (
    <>
      <div className={blockFileTitle}>
        <Text type="body1SemiBold">{t("project.dashboard.editor.image.file.label")}</Text>
        {!block?.config.url && (
          <Button variant="tertiary" slim leftIcon={<AddCircleLine />} onClick={openModal}>
            {t("project.dashboard.editor.image.file.action")}
          </Button>
        )}
      </div>
      {block?.config.url && block.config.url.length > 0 && (
        <div className={blockFileInfo}>
          <Text
            type="body2"
            className={blockFileInfoName}
            title={block.config.url.split("/").pop()}
          >
            {block.config.url.split("/").pop()}
          </Text>
          <Text type="body2" className={blockFileInfoSize}>
            {/* PerformanceEntry typing issue workaround */}
            {fileSize} ko
          </Text>
          <TooltipV2 overlay={t("project.dashboard.actions.replace")}>
            <ButtonIconV2 className={blockFileInfoUpload} onClick={openModal}>
              <UploadLine size="s" />
            </ButtonIconV2>
          </TooltipV2>
          {deletable && (
            <TooltipV2 overlay={t("project.dashboard.actions.delete")}>
              <ButtonIconV2 className={blockFileInfoDelete} onClick={removeImage}>
                <DeleteBinLine size="s" />
              </ButtonIconV2>
            </TooltipV2>
          )}
        </div>
      )}
      <BlockFileUploadModal
        projectId={projectId ?? ""}
        opened={modalOpened}
        onClose={closeModal}
        onUploadSucceeded={({ path }) => {
          updatePath(path);
        }}
      />
    </>
  );
}

const blockFileTitle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.carbon900};
`;

const blockFileInfo = css`
  align-items: start;
  display: grid;
  grid-template-columns: 1fr repeat(2, max-content);
  grid-auto-rows: 1fr;
  column-gap: ${spacings.xs};
`;

const blockFileInfoName = css`
  color: ${colors.carbon900};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  grid-column: 1 / span 1;
`;

const blockFileInfoSize = css`
  color: ${colors.carbon800};
  grid-column: 1 / span 1;
  grid-row: 2 / span 1;
`;

const blockFileInfoUpload = css`
  color: ${colors.carbon800};
  grid-column: 2 / span 1;
  grid-row: 1 / span 2;
`;

const blockFileInfoDelete = css`
  color: ${colors.carbon800};
  grid-column: 3 / span 1;
  grid-row: 1 / span 2;
`;
